import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Localization from 'expo-localization';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { logger } from '@/src/core/utils';

import en from './locales/en.json';
import es from './locales/es.json';

const LANGUAGE_STORAGE_KEY = '@tournaly_language';

export type SupportedLanguage = 'en' | 'es';

export interface LanguageOption {
  code: SupportedLanguage;
  name: string;
  nativeName: string;
}

export const SUPPORTED_LANGUAGES: LanguageOption[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
];

type TranslationKeys = typeof en;

const translations: Record<SupportedLanguage, TranslationKeys> = {
  en,
  es,
};

interface LanguageContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function getNestedValue(obj: any, path: string): string | undefined {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

function interpolate(text: string, params?: Record<string, string | number>): string {
  if (!params) return text;

  return Object.entries(params).reduce((result, [key, value]) => {
    return result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), String(value));
  }, text);
}

function getDeviceLanguage(): SupportedLanguage {
  const deviceLocale = Localization.getLocales()[0]?.languageCode || 'en';

  // Check if device language is supported
  if (SUPPORTED_LANGUAGES.some(lang => lang.code === deviceLocale)) {
    return deviceLocale as SupportedLanguage;
  }

  return 'en'; // Default to English
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<SupportedLanguage>('en');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadLanguagePreference();
  }, []);

  const loadLanguagePreference = async () => {
    try {
      const savedLanguage = await AsyncStorage.getItem(LANGUAGE_STORAGE_KEY);

      if (savedLanguage && SUPPORTED_LANGUAGES.some(lang => lang.code === savedLanguage)) {
        setLanguageState(savedLanguage as SupportedLanguage);
        logger.debug('i18n', 'Loaded language preference', { language: savedLanguage });
      } else {
        // Use device language as default
        const deviceLang = getDeviceLanguage();
        setLanguageState(deviceLang);
        logger.debug('i18n', 'Using device language', { language: deviceLang });
      }
    } catch (error) {
      logger.error('i18n', 'Failed to load language preference', { error });
    } finally {
      setIsLoading(false);
    }
  };

  const saveLanguagePreference = async (lang: SupportedLanguage) => {
    try {
      await AsyncStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      logger.debug('i18n', 'Saved language preference', { language: lang });
    } catch (error) {
      logger.error('i18n', 'Failed to save language preference', { error });
    }
  };

  const setLanguage = useCallback((lang: SupportedLanguage) => {
    setLanguageState(lang);
    saveLanguagePreference(lang);
  }, []);

  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    const translation = getNestedValue(translations[language], key);

    if (translation === undefined) {
      // Fallback to English if key not found in current language
      const fallback = getNestedValue(translations.en, key);
      if (fallback === undefined) {
        logger.warn('i18n', 'Missing translation key', { key });
        return key; // Return the key itself as last resort
      }
      return interpolate(fallback, params);
    }

    return interpolate(translation, params);
  }, [language]);

  if (isLoading) {
    return null;
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, isLoading }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}

// Alias for more intuitive usage
export const useTranslation = useLanguage;
